import React, { useState } from "react";
import { Button } from "reactstrap";
import { Coins, Calendar, Star, EyeOff, Save, X } from "lucide-react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import { updatePartnerOffer } from "../../../slices/RedemptionOffer/UpdateOffer/thunk";

function OfferView({ offer, onClose, onEdit, onUnpublished }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [publishing, setPublishing] = useState(false);

  const operatorPartnerId = localStorage.getItem("operatorId");

  const partnerName = localStorage.getItem("userName");

  const [operatorId, partnerId] = operatorPartnerId
    ? operatorPartnerId.split("/")
    : [null, null];

  if (!offer) return null;

  const formatDiscount = () => {
    if (offer.discountValue === null || offer.discountValue === undefined) {
      return "-";
    }

    if (offer.discountType?.toLowerCase() === "percentage") {
      return `${offer.discountValue}%`;
    }

    return offer.discountValue;
  };

  const formatDate = (date) => {
    if (!date) return "-";

    return new Date(date).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const handleUnpublish = () => {
    if (!offer?.id) {
      toast.error("Offer ID not found");
      return;
    }

    const bannerImageId =
      typeof offer.bannerImage === "object"
        ? offer.bannerImage?.id ||
          offer.bannerImage?.imageId ||
          offer.bannerImage?.fileId ||
          null
        : offer.bannerImage || null;

    const payload = {
      offerTitle: offer.offerTitle || "",
      description: offer.description || "",
      category: offer.category || "",
      customCategory: null,

      bannerImage: bannerImageId
        ? {
            id: bannerImageId,
          }
        : null,

      partnerId: offer.partnerId || partnerId,

      partnerName: offer.partnerName || partnerName,

      operatorId: offer.operatorId || operatorId,

      pointsRequired: Number(offer.pointsRequired || 0),

      discountType: offer.discountType || "flat",

      discountValue: Number(offer.discountValue || 0),

      minOrderValue: Number(offer.minOrderValue ?? offer.minimumSpend ?? 0),

      validityStart: offer.validityStart
        ? new Date(offer.validityStart).toISOString()
        : null,

      validityEnd: offer.validityEnd
        ? new Date(offer.validityEnd).toISOString()
        : null,

      status: "draft",
    };

    dispatch(
      updatePartnerOffer(
        payload,
        offer.id,
        setPublishing,
        () => {
          onUnpublished();
        },
        "Offer unpublished successfully",
      ),
    );
  };

  return (
    <div className="offer-view-wrapper">
      <div className="offer-view-scroll">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center offer-header">
          <div className="d-flex align-items-center gap-2">
            <span className="status-badge">
              <span className="status-dot" />
              {offer.status
                ? offer.status.charAt(0).toUpperCase() +
                  offer.status.slice(1).toLowerCase()
                : "-"}
            </span>

            <span className="offer-id">#{offer.id || "-"}</span>
          </div>

          <div className="d-flex align-items-center gap-2">
            <span className="dots">•••</span>

            <X
              size={16}
              className="close-icon"
              onClick={onClose}
              role="button"
            />
          </div>
        </div>

        <div className="offer-body">
          {/* Title */}
          <h4 className="offer-title">{offer.offerTitle || "-"}</h4>

          <span className="category-badge">{offer.category || "-"}</span>

          <p className="offer-desc">{offer.description || "-"}</p>

          {/* Stats */}
          <div className="stats-section">
            <div className="stats-row">
              {/* Points */}
              <div className="stat-col">
                <div className="stat-label">POINTS</div>

                <div className="stat-value">
                  <Coins size={16} className="stat-icon" />

                  {offer.pointsRequired ?? "-"}
                </div>

                <div className="stat-sub">required</div>
              </div>

              {/* Discount */}
              <div className="stat-col">
                <div className="stat-label">DISCOUNT</div>

                <div className="stat-value">{formatDiscount()}</div>

                <div className="stat-sub">
                  {offer.minimumSpend
                    ? `min ₹${offer.minimumSpend}`
                    : "minimum spend"}
                </div>
              </div>

              {/* Redeemed */}
              {/* <div className="stat-col">
                <div className="stat-label">REDEEMED</div>

                <div className="stat-value">{offer.redeemed ?? 0}</div>

                <div className="stat-sub">times</div>
              </div> */}
            </div>
          </div>

          {/* Validity */}
          <div className="validity-section">
            <div className="validity-row">
              <div className="validity-icon-box">
                <Calendar size={18} />
              </div>

              <div>
                <div className="validity-label">VALIDITY PERIOD</div>

                <div className="validity-dates">
                  {formatDate(offer.validityStart)}

                  <span className="arrow">{" → "}</span>

                  {formatDate(offer.validityEnd)}
                </div>
              </div>
            </div>
          </div>

          {/* Total Points Redeemed */}
          {/* <div className="total-points-box">
            <div className="total-points-icon">
              <Star size={18} fill="#F97316" color="#F97316" />
            </div>

            <div>
              <div className="total-points-label">TOTAL POINTS REDEEMED</div>

              <div className="total-points-value">
                {offer.totalPointsRedeemed ?? 0}
              </div>
            </div>
          </div> */}
        </div>
      </div>

      {/* Footer */}
      <div className="offer-footer">
        <Button
          outline
          className="btn-unpublish"
          onClick={handleUnpublish}
          disabled={publishing}
        >
          <EyeOff size={16} className="me-2" />
          Unpublish
        </Button>

        <Button className="btn-edit" onClick={() => onEdit?.(offer)}>
          <Save size={16} className="me-2" />
          Edit Offer
        </Button>
      </div>

      {/* KEEP YOUR EXISTING STYLE HERE */}
      <style>{`
        .offer-view-wrapper {
          display: flex;
          flex-direction: column;
          height: 100%;
          max-height: 100vh;
          background: #fff;
          border-radius: 12px;
          overflow: hidden;
          font-family: inherit;
        }

        .offer-view-scroll {
          flex: 1 1 auto;
          overflow-y: auto;
        }

        .offer-header {
          padding: 16px 20px;
          border-bottom: 1px solid #F1F1F1;
        }

        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          background: #E7F7EF;
          color: #1F9254;
          font-weight: 600;
          font-size: 13px;
          padding: 4px 12px;
          border-radius: 20px;
        }

        .status-dot {
          width: 6px;
          height: 6px;
          background: #1F9254;
          border-radius: 50%;
          display: inline-block;
        }

        .offer-id {
          color: #9CA3AF;
          font-size: 13px;
          font-weight: 500;
        }

        .dots {
          color: #9CA3AF;
          font-size: 14px;
          cursor: pointer;
        }

        .close-icon {
          color: #9CA3AF;
          cursor: pointer;
        }

        .offer-body {
          padding: 20px;
        }

        .offer-title {
          font-family: Georgia, "Times New Roman", serif;
          font-weight: 700;
          font-size: 22px;
          margin-bottom: 10px;
          color: #111827;
        }

        .category-badge {
          display: inline-block;
          background: #FDECE3;
          color: #EA580C;
          font-size: 13px;
          font-weight: 500;
          padding: 4px 12px;
          border-radius: 8px;
          margin-bottom: 14px;
        }

        .offer-desc {
          color: #6B7280;
          font-size: 14px;
          line-height: 1.5;
          margin-bottom: 20px;
        }

        .stats-section {
          border-top: 1px solid #EEF0F2;
          border-bottom: 1px solid #EEF0F2;
          padding: 16px 0;
          margin-bottom: 20px;
        }

        .stats-row {
          display: flex;
        }

        .stat-col {
          flex: 1;
          padding: 0 12px;
        }

        .stat-col:not(:last-child) {
          border-right: 1px solid #EEF0F2;
        }

        .stat-col:first-child {
          padding-left: 0;
        }

        .stat-label {
          color: #9CA3AF;
          font-size: 11px;
          font-weight: 600;
          letter-spacing: 0.5px;
          margin-bottom: 6px;
        }

        .stat-value {
          display: flex;
          align-items: center;
          gap: 4px;
          font-size: 20px;
          font-weight: 700;
          color: #111827;
        }

        .stat-icon {
          color: #F97316;
          flex-shrink: 0;
        }

        .stat-sub {
          font-size: 12px;
          color: #9CA3AF;
          margin-top: 2px;
        }

        .validity-section {
          border-top: 1px solid #EEF0F2;
          border-bottom: 1px solid #EEF0F2;
          padding: 16px 0;
          margin-bottom: 20px;
        }

        .validity-row {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .validity-icon-box {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: #F3F4F6;
          color: #6B7280;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .validity-label {
          font-size: 11px;
          font-weight: 600;
          color: #9CA3AF;
          letter-spacing: 0.5px;
          margin-bottom: 3px;
        }

        .validity-dates {
          font-size: 15px;
          font-weight: 600;
          color: #111827;
        }

        .arrow {
          color: #9CA3AF;
          font-weight: 400;
        }

        .total-points-box {
          display: flex;
          align-items: center;
          gap: 12px;
          background: #FDF3EC;
          border: 1px solid #FBE3D2;
          border-radius: 10px;
          padding: 14px 16px;
        }

        .total-points-icon {
          width: 36px;
          height: 36px;
          border-radius: 8px;
          background: #FCE2CB;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .total-points-label {
          font-size: 11px;
          font-weight: 600;
          color: #EA580C;
          letter-spacing: 0.5px;
          margin-bottom: 3px;
        }

        .total-points-value {
          font-size: 20px;
          font-weight: 700;
          color: #EA580C;
        }

        .offer-footer {
          flex-shrink: 0;
          display: flex;
          gap: 12px;
          padding: 14px 20px;
          background: #FAFAFA;
          border-top: 1px solid #EEF0F2;
        }

        .btn-unpublish {
          flex: 1;
          color: #DC2626 !important;
          border-color: #FCA5A5 !important;
          background: #fff !important;
          font-weight: 500;
          border-radius: 10px !important;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .btn-edit {
          flex: 1;
          background: #F97316 !important;
          border-color: #F97316 !important;
          color: #fff !important;
          font-weight: 500;
          border-radius: 10px !important;
          display: flex;
          align-items: center;
          justify-content: center;
        }

        @media (max-width: 360px) {
          .offer-footer {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}

export default OfferView;
